import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/contexts/AuthContext';
import {
  getMeeting,
  updateActionItem,
} from '@/api/meetings';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Meeting, ActionItem, AgendaItem } from '@/types/Meeting';
import { PreviousActionList } from '@/components/PreviousActionList';
import { Checkbox } from '@/components/ui/checkbox';
import 'react-quill/dist/quill.bubble.css';

// Read-only version of the ActionList component
function ReadOnlyActionList({ actions, onUpdateActionItem }: { 
  actions: ActionItem[];
  onUpdateActionItem: (actionItemId: string, updatedItem: { text: string; completed: boolean, hashtags: string[], order: number }) => void;
}) {
  const sortedActions = [...actions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const renderHashtagText = (text: string, hashtags: string[]) => {
    const parts = text.split(/(#\w+)/g);
    return (
      <span>
        {parts.map((part, index) => {
          const tag = part.startsWith('#') ? part.slice(1) : null;
          if (tag && hashtags.includes(tag)) {
            return (
              <span key={index} className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded-full text-sm">
                {part}
              </span>
            );
          }
          return part;
        })}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {sortedActions.map((item) => (
          <div 
            key={item._id} 
            className="flex items-start gap-4 p-4 rounded-lg bg-card hover:bg-secondary/50 transition-colors"
          >
            <Checkbox
              checked={item.completed}
              onCheckedChange={(checked) => 
                onUpdateActionItem(item._id, { 
                  ...item, 
                  completed: checked as boolean,
                  text: item.text,
                  hashtags: item.hashtags,
                  order: item.order ?? 0
                })
              } 
              className="mt-1 rounded border-primary"
            />
            <div 
              className={`flex-1 break-words ${
                item.completed ? 'line-through text-muted-foreground' : ''
              }`}
            >
              {renderHashtagText(item.text, item.hashtags)}
            </div>
          </div>
        ))}
        {sortedActions.length === 0 && (
          <div className="text-center text-muted-foreground py-4">
            No actions for this meeting
          </div>
        )}
      </div>
    </div>
  );
}

// Read-only version of the AgendaList component
function ReadOnlyAgendaList({ agenda }: { agenda: AgendaItem[] }) {
  const sortedAgenda = [...agenda].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="space-y-3">
      {sortedAgenda.map((item) => (
        <div key={item._id} className="flex items-start gap-4 p-4 rounded-lg bg-card">
          <Checkbox
            checked={item.completed}
            disabled={true}
            className="mt-1 rounded border-primary"
          />
          <div className={`flex-1 break-words ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
            {item.text}
          </div>
        </div>
      ))}
      {sortedAgenda.length === 0 && (
        <div className="text-center text-muted-foreground py-4">No agenda items for this meeting</div>
      )}
    </div>
  );
}

export function ReadOnlyMeetingDetail() {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompletedPrevious, setShowCompletedPrevious] = useState(false);
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMounted = useRef(false);

  useEffect(() => {
    const fetchMeeting = async () => {
      setLoading(true);
      try {
        const response = await getMeeting(id!);
        setMeeting(response.meeting);
      } catch (error: any) {
        setError(error.message);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message === 'Access denied'
            ? "You don't have permission to view this meeting"
            : "Failed to load meeting details"
        });
        if (error.message === 'Access denied') {
          navigate('/app/meetings');
        }
      } finally {
        setLoading(false);
      }
    };
   
    if (!isMounted.current) {
      isMounted.current = true;
    }
   
    fetchMeeting();
  }, [id, toast, navigate]);

  const { checkSubscriptionStatus } = useAuth();
  
  const handleUpdateActionItem = async (actionItemId: string, updatedItem: { text: string; completed: boolean; hashtags: string[], order: number }) => {
    if (meeting && meeting.id) {
      try {
        // Check subscription only when toggling action item status
        // We only need to verify the user has a valid account, not what tier they're on
        // since free tier users can toggle action items
        await checkSubscriptionStatus();
        
        const response = await updateActionItem(meeting.id, actionItemId, updatedItem);
        setMeeting({
          ...meeting,
          actions: response.meeting.actions,
        });
      } catch (error) {
        console.error('Error updating action item:', error);
        toast({ variant: 'destructive', title: 'Error', description: 'Failed to update action item' });
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-destructive">{error}</div>;
  if (!meeting) return <div className="text-center">Meeting not found</div>;

  return (
    <div className="space-y-8">
      {meeting && (
        <>
          <div className="space-y-4">
            {/* Title - Read Only */}
            <div className="md:flex md:justify-between md:items-center">
              <h1 className="text-3xl font-bold text-foreground lg:text-4xl">
                {meeting.title}
              </h1>
            </div>
            
            {/* Date/time display - Read Only */}
            <div className="flex items-center gap-2">
              <div className="flex items-center px-3 py-1 rounded-md border">
                <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                {format(new Date(meeting.date), "PPP")}
              </div>
              <div className="flex items-center px-3 py-1 rounded-md border">
                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                {format(new Date(meeting.date), "HH:mm")}
              </div>
            </div>
          </div>
  
          {/* Shared with - Read Only */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Shared with</h2>
            <div className="flex flex-wrap gap-2">
              {meeting.sharedWith.map((email) => (
                <div key={email} className="flex items-center gap-2 px-3 py-1 bg-secondary rounded-full">
                  <span>{email}</span>
                </div>
              ))}
              {meeting.sharedWith.length === 0 && (
                <div className="text-muted-foreground">Not shared with anyone</div>
              )}
            </div>
          </div>
  
          {/* Agenda - Read Only */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Agenda</h2>
            <ReadOnlyAgendaList agenda={meeting.agenda} />
          </div>
  
          {/* Notes - Read Only */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Notes</h2>
            <div 
              className="read-only-notes ql-editor bg-background/50 rounded-lg p-4 min-h-[200px] border"
              dangerouslySetInnerHTML={{ __html: meeting.notes }}
            />
          </div>
  
          {/* Actions - Read Only except for toggling */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Actions</h2>
            <ReadOnlyActionList
              actions={meeting.actions}
              onUpdateActionItem={handleUpdateActionItem}
            />
          </div>
  
          {/* Previous Actions Section */}
          <div className="previous-actions-container">
            <PreviousActionList 
              currentMeetingId={meeting.id}
              showCompleted={showCompletedPrevious}
              onShowCompletedChange={setShowCompletedPrevious}
            />
          </div>
        </>
      )}
    </div>
  );
}
